<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'
import { rangeForPreset } from '@/composables/date-range-presets'

import ConversationEdit from '@/components/shared/ConversationEdit.vue'
import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
})

const { t } = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()

const defaultPreset = webSiteStore.getDefaultTimelinePeriod() ?? portalStore.dateRangePresetEnum.MONTH
const initialRange = rangeForPreset(defaultPreset) ?? rangeForPreset(portalStore.dateRangePresetEnum.MONTH)
const range = ref({
  from: initialRange.from.format('YYYY-MM-DD'),
  to: initialRange.to.format('YYYY-MM-DD'),
})

const conversations = ref([])
const loading = ref(true)
const editItem = ref(null)

const conversationTypeMeta = portalStore.getLocalizedConversationTypes()
const metaFor = (type) => conversationTypeMeta.find(m => m.id === type)

const entries = computed(() => conversations.value.map((conversation) => {
  const meta = metaFor(conversation.conversation_type)
  return {
    id: conversation.id,
    icon: meta?.icon,
    color: meta?.color,
    title: conversation.contact_name || meta?.name,
    subtitle: `${meta?.name} · ${dayjs(conversation.occurred_at).format('L LT')}`,
    note: conversation.note,
    raw: conversation,
  }
}))

const load = async() => {
  loading.value = true
  const { data } = await planningStore.fetchConversationsByCompanyAsync(props.company.id, {
    date_from: range.value.from,
    date_to: range.value.to,
  })
  conversations.value = data
  loading.value = false
}

const onRangeChange = (value) => {
  range.value = value
  load()
}

const onLog = () => {
  editItem.value = {
    company: props.company.id,
    conversation_type: portalStore.getLocalizedConversationTypes()[0].id,
    occurred_at: dayjs().toISOString(),
    note: '',
  }
}

const onEntryClick = (entry) => { editItem.value = { ...entry.raw } }

const onSaved = async() => {
  editItem.value = null
  await load()
}

onMounted(load)
</script>

<template>
  <div class="tab-root" :class="{ 'tab-root--fill': isMobile }">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      class="dialog-sm"
      @hide="editItem = null"
    >
      <conversation-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :company="props.company"
        @update="onSaved"
        @cancel="editItem = null"
      />
    </q-dialog>
    <div class="q-px-sm q-pt-sm">
      <date-range-navigator
        :model-value="range"
        :default-preset="defaultPreset"
        @update:model-value="onRangeChange"
      />
    </div>
    <div class="row items-center justify-end q-px-sm q-mb-sm">
      <q-btn
        style="padding: 0;"
        size="md"
        unelevated
        ripple
        rounded
        icon="mdi-plus-circle-outline"
        @click="onLog"
      />
    </div>

    <div class="timeline-scroll" :class="{ 'timeline-scroll--bounded': !isMobile }">
      <q-inner-loading :showing="loading" />

      <div v-if="!loading && !entries.length" class="text-grey-6 text-caption q-pa-md text-center">
        {{ t('common.noData') }}
      </div>

      <q-timeline
        v-else-if="!loading"
        layout="dense"
        color="primary"
        class="q-pl-md q-pr-sm"
      >
        <q-timeline-entry
          v-for="entry in entries"
          :key="entry.id"
          :title="entry.title"
          :subtitle="entry.subtitle"
          :icon="entry.icon"
          :color="entry.color"
          class="cursor-pointer"
          @click="onEntryClick(entry)"
        >
          <div v-if="entry.note" class="text-caption">{{ entry.note }}</div>
        </q-timeline-entry>
      </q-timeline>
    </div>
  </div>
</template>

<style scoped>
.tab-root {
  position: relative;
  min-height: 20rem;
}
.tab-root--fill {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.timeline-scroll {
  position: relative;
}
.tab-root--fill .timeline-scroll {
  flex: 1 1 auto;
  min-height: 0;
}
.timeline-scroll--bounded {
  max-height: 28rem;
  overflow-y: auto;
}
</style>
